import React, { useState, useEffect } from 'react';
import { invoiceAPI } from '../utils/api';

function InvoiceHistory() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await invoiceAPI.getAll();
      setInvoices(response.data);
      setError(null);
    } catch (err) {
      // Database is disabled, so this is expected
      if (err.response && err.response.data && err.response.data.message) {
        setError('Database storage is disabled. Invoice history is not available.');
      } else {
        setError('Failed to load invoices. Database storage is disabled.');
      }
      setInvoices([]); // Set empty array instead of crashing
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber, date) => {
    try {
      setDownloading(invoiceId);
      const response = await invoiceAPI.getPDF(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoiceNumber}_${date}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download PDF. Please try again.');
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <p className="text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={fetchInvoices}
              className="px-4 py-2 bg-bluestar-600 text-white rounded-md hover:bg-bluestar-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-blue-700">Invoice History</h1>
          <p className="text-gray-600">
            Total Invoices: <span className="font-semibold">{invoices.length}</span>
          </p>
        </div>

        {invoices.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg mb-4">No invoices found.</p>
            <p className="text-gray-500">Create your first invoice to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <thead className="bg-bluestar-50">
                <tr>
                  <th className="px-4 py-3 border border-gray-300 text-left text-sm font-semibold text-gray-700">
                    Invoice Number
                  </th>
                  <th className="px-4 py-3 border border-gray-300 text-left text-sm font-semibold text-gray-700">
                    Date
                  </th>
                  <th className="px-4 py-3 border border-gray-300 text-left text-sm font-semibold text-gray-700">
                    Bill To
                  </th>
                  <th className="px-4 py-3 border border-gray-300 text-left text-sm font-semibold text-gray-700">
                    PO Number
                  </th>
                  <th className="px-4 py-3 border border-gray-300 text-right text-sm font-semibold text-gray-700">
                    Total Amount
                  </th>
                  <th className="px-4 py-3 border border-gray-300 text-center text-sm font-semibold text-gray-700">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 border border-gray-300 font-semibold">
                      {invoice.invoiceNumber}
                    </td>
                    <td className="px-4 py-3 border border-gray-300">
                      {formatDate(invoice.date)}
                    </td>
                    <td className="px-4 py-3 border border-gray-300">
                      <div className="max-w-xs truncate" title={invoice.billTo}>
                        {invoice.billTo}
                      </div>
                    </td>
                    <td className="px-4 py-3 border border-gray-300">
                      {invoice.poNumber || '-'}
                    </td>
                    <td className="px-4 py-3 border border-gray-300 text-right font-semibold">
                      {formatCurrency(invoice.grandTotal)}
                    </td>
                    <td className="px-4 py-3 border border-gray-300 text-center">
                      <button
                        onClick={() =>
                          handleDownloadPDF(
                            invoice._id,
                            invoice.invoiceNumber,
                            formatDate(invoice.date)
                          )
                        }
                        disabled={downloading === invoice._id}
                        className={`px-4 py-2 rounded-md text-sm ${
                          downloading === invoice._id
                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            : 'bg-bluestar-600 text-white hover:bg-bluestar-700'
                        } focus:outline-none focus:ring-2 focus:ring-bluestar-500`}
                      >
                        {downloading === invoice._id ? 'Downloading...' : 'View PDF'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default InvoiceHistory;

